/**
 * Profile Selector Component
 *
 * DC-324: Dropdown to select assessment profile for AI Readiness calculation
 *
 * Features:
 * - Fetches available profiles from API
 * - Displays profile name, problem type, and description
 * - Supports custom organization profiles
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import './ai-readiness.css';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Assessment profile from API
 */
export interface AssessmentProfile {
  id: string;
  name: string;
  description?: string;
  problem_type?: string;
  statistical_health_weight: number;
  semantic_maturity_weight: number;
  governance_fair_weight: number;
  is_system_default?: boolean;
}

export interface ProfileSelectorProps {
  /** Currently selected profile ID */
  selectedProfileId?: string | null;
  /** Callback when profile selection changes */
  onProfileChange: (profileId: string | null, profile: AssessmentProfile | null) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Optional organization ID to filter profiles */
  organizationId?: string;
  /** Optional additional CSS class */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  selectedProfileId,
  onProfileChange,
  disabled = false,
  organizationId,
  className = '',
}) => {
  const [profiles, setProfiles] = useState<AssessmentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const onProfileChangeRef = useRef(onProfileChange);
  onProfileChangeRef.current = onProfileChange;

  // Fetch profiles on mount
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = organizationId
          ? `/api/ai-readiness/profiles?organizationId=${encodeURIComponent(organizationId)}`
          : '/api/ai-readiness/profiles';

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch profiles: ${response.statusText}`);
        }

        const data = await response.json();
        setProfiles(data.profiles || []);

        // Auto-select default profile if none selected (only on initial load)
        if (!hasInitializedRef.current && !selectedProfileId && data.profiles?.length > 0) {
          hasInitializedRef.current = true;
          const defaultProfile = data.profiles.find(
            (p: AssessmentProfile) => p.is_system_default
          ) || data.profiles[0];
          onProfileChangeRef.current(defaultProfile.id, defaultProfile);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profiles');
        console.error('Error fetching assessment profiles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [organizationId, selectedProfileId]);

  // Handle profile selection
  const handleValueChange = (value: string) => {
    if (value === '__none__') {
      onProfileChange(null, null);
      return;
    }

    const selectedProfile = profiles.find((p) => p.id === value) || null;
    onProfileChange(value, selectedProfile);
  };

  // Get display value for selected profile
  const getSelectedProfile = (): AssessmentProfile | undefined => {
    return profiles.find((p) => p.id === selectedProfileId);
  };

  // Format weight display
  const formatWeights = (profile: AssessmentProfile): string => {
    const sh = Math.round(profile.statistical_health_weight * 100);
    const sm = Math.round(profile.semantic_maturity_weight * 100);
    const gf = Math.round(profile.governance_fair_weight * 100);
    return `SH:${sh}% SM:${sm}% GF:${gf}%`;
  };

  if (error) {
    return (
      <div className={`ai-profile-selector ai-profile-error ${className}`}>
        <span className="ai-profile-error-icon">⚠</span>
        <span className="ai-profile-error-text">{error}</span>
      </div>
    );
  }

  return (
    <div className={`ai-profile-selector ${className}`}>
      <label className="ai-profile-label">Assessment Profile</label>
      <Select
        value={selectedProfileId || '__none__'}
        onValueChange={handleValueChange}
        disabled={disabled || loading}
      >
        <SelectTrigger className="ai-profile-trigger">
          <SelectValue placeholder={loading ? 'Loading profiles...' : 'Select a profile'}>
            {getSelectedProfile()?.name || 'Select a profile'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="ai-profile-content">
          {profiles.map((profile) => (
            <SelectItem
              key={profile.id}
              value={profile.id}
              className="ai-profile-item"
            >
              <div className="ai-profile-item-content">
                <div className="ai-profile-item-header">
                  <span className="ai-profile-item-name">{profile.name}</span>
                  {profile.problem_type && (
                    <span className="ai-profile-item-type">{profile.problem_type}</span>
                  )}
                  {profile.is_system_default && (
                    <span className="ai-profile-item-default">Default</span>
                  )}
                </div>
                {profile.description && (
                  <div className="ai-profile-item-description">{profile.description}</div>
                )}
                <div className="ai-profile-item-weights">{formatWeights(profile)}</div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Show selected profile weights summary */}
      {getSelectedProfile() && (
        <div className="ai-profile-summary">
          <span className="ai-profile-summary-label">Weights:</span>
          <span className="ai-profile-summary-weights">
            {formatWeights(getSelectedProfile()!)}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProfileSelector;
